/* Macro photographs describe a material AND a colour; they never create variants. */
(function (root) {
  'use strict';
  // Colour names and macro photos come from the Shopify Coloris entries: a photo belongs to one material, a name may come from any material.
  const colorisFor = (list, material, value) => list.find(entry => entry.material === material && entry.color === value) || null;
  const colorisName = (list, material, value) => (colorisFor(list, material, value) || list.find(entry => entry.color === value))?.name || null;
  function candidates(variants, selections, order, position, value) {
    const rank = order.indexOf(position);
    return variants.filter(variant => variant.options[position] === value && order.slice(0, rank).every(index => selections[index] && variant.options[index] === selections[index]));
  }
  function reconcileSelection(variants, selections, order, position) {
    const next = [...selections];
    for (const index of order.slice(order.indexOf(position) + 1)) {
      if (candidates(variants, next, order, index, next[index]).length) continue;
      const preceding = order.slice(0, order.indexOf(index));
      const matches = variants.filter(variant => preceding.every(i => variant.options[i] === next[i]));
      const replacement = matches.find(variant => variant.available) || matches[0];
      next[index] = replacement?.options[index] || '';
    }
    return next;
  }
  function mount(host, { variants, options }) {
    const container = host.querySelector('[data-product-options]');
    if (!container?.querySelector('[data-option-kind]')) return null;
    const order = [...container.querySelectorAll('[data-option-position]')].map(select => Number(select.dataset.optionPosition));
    let coloris = [];
    try { coloris = JSON.parse(container.querySelector('[data-coloris]')?.textContent || '[]'); } catch { /* Text labels remain usable. */ }
    const groups = [];
    const feedback = container.querySelector('[data-variant-feedback]');
    const values = () => options.map(select => select.value);
    for (const select of options) {
      const wrapper = select.closest('[data-option-kind]');
      if (!wrapper || wrapper.dataset.optionKind === 'other') continue;
      const kind = wrapper.dataset.optionKind;
      const label = wrapper.querySelector('label');
      const group = document.createElement('div');
      group.className = `variant-choices variant-choices--${kind}`;
      group.setAttribute('role', 'radiogroup');
      group.setAttribute('aria-labelledby', label.id);
      const buttons = [...select.options].filter(option => option.value).map(option => {
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'variant-choice'; button.dataset.value = option.value;
        button.setAttribute('role', 'radio');
        const text = document.createElement('span'); text.className = 'variant-choice-name'; text.textContent = option.textContent.trim();
        option.dataset.label = option.textContent.trim();
        if (kind === 'color') {
          const tile = document.createElement('span'); tile.className = 'variant-swatch'; tile.setAttribute('aria-hidden', 'true');
          const img = document.createElement('img'); img.width = 56; img.height = 56; img.alt = ''; img.decoding = 'async'; img.hidden = true;
          img.addEventListener('error', () => { img.hidden = true; tile.classList.add('variant-swatch--missing'); button.classList.add('variant-choice--text'); });
          tile.append(img); button.append(tile);
        }
        button.append(text);
        button.addEventListener('click', () => { select.value = option.value; select.dispatchEvent(new Event('change', { bubbles: true })); });
        group.append(button); return button;
      });
      group.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        const enabled = buttons.filter(button => !button.hidden && !button.disabled);
        if (!enabled.length) return;
        event.preventDefault();
        const index = enabled.indexOf(document.activeElement);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? enabled.length - 1 : (index + (['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1) + enabled.length) % enabled.length;
        enabled[next].click(); enabled[next].focus();
      });
      const placeholder = document.createElement('option'); placeholder.value = ''; placeholder.textContent = container.dataset.chooseLabel; placeholder.disabled = true;
      select.append(placeholder); select.hidden = true; wrapper.append(group);
      label.removeAttribute('for');
      groups.push({ select, wrapper, group, buttons, kind });
    }
    // Each material shows its lowest price when the materials of the fiche are not priced alike.
    const materialGroup = groups.find(group => group.kind === 'material');
    if (materialGroup) {
      const position = Number(materialGroup.select.dataset.optionPosition);
      const lowest = materialGroup.buttons.map(button => Math.min(...variants.filter(variant => variant.options[position] === button.dataset.value).map(variant => variant.price)));
      if (new Set(lowest).size > 1 && lowest.every(Number.isFinite)) {
        const digits = lowest.every(price => price % 100 === 0) ? 0 : 2;
        const format = new Intl.NumberFormat(root.document?.documentElement.lang || 'fr', { style: 'currency', currency: container.dataset.currency || 'EUR', minimumFractionDigits: digits, maximumFractionDigits: digits });
        materialGroup.buttons.forEach((button, index) => {
          const price = root.document.createElement('span'); price.className = 'variant-choice-price';
          price.textContent = (container.dataset.fromLabel || '{{ price }}').replace('{{ price }}', format.format(lowest[index] / 100));
          button.append(price);
        });
      }
    }
    const fabric = host.querySelector('[data-gallery-fabric]');
    // Photos of the fiche's other materials are fetched while idle, so switching material shows them at once.
    const materials = new Set([container.dataset.fabric, ...(groups.find(group => group.kind === 'material')?.buttons || []).map(button => button.dataset.value)]);
    if (typeof root.Image === 'function') (root.requestIdleCallback || (callback => setTimeout(callback, 200)))(() => coloris.forEach(entry => {
      if (!entry.src || !materials.has(entry.material)) return;
      const image = new root.Image(); image.decoding = 'async'; image.srcset = entry.srcset || ''; image.src = entry.src;
    }));
    function sync() {
      const selections = values();
      const material = groups.find(group => group.kind === 'material');
      const materialName = material ? material.select.value : container.dataset.fabric;
      for (const { select, buttons, kind } of groups) {
        if (kind !== 'color') continue;
        for (const button of buttons) {
          const option = [...select.options].find(item => item.value === button.dataset.value);
          const name = colorisName(coloris, materialName, button.dataset.value) || option.dataset.label;
          button.querySelector('.variant-choice-name').textContent = name;
          option.textContent = name;
        }
      }
      for (const { select, wrapper, buttons, kind } of groups) {
        const position = Number(select.dataset.optionPosition);
        wrapper.querySelector('[data-option-current]').textContent = select.value ? ` · ${select.selectedOptions[0].textContent.trim()}` : '';
        for (const button of buttons) {
          const matches = candidates(variants, selections, order, position, button.dataset.value);
          const checked = button.dataset.value === select.value;
          button.disabled = !matches.length;
          button.hidden = !matches.length;
          button.setAttribute('aria-checked', String(checked));
          button.tabIndex = checked && !button.disabled ? 0 : -1;
          const soldOut = matches.length && !matches.some(variant => variant.available);
          button.classList.toggle('is-sold-out', Boolean(soldOut));
          // OEM+ colours not listed for the vehicle are made to order (2 to 3 weeks); only shown when every matching variant is.
          const onOrder = kind === 'color' && matches.length > 0 && matches.every(variant => variant.on_order);
          button.classList.toggle('is-on-order', onOrder);
          let lead = button.querySelector('.variant-choice-lead');
          if (onOrder && !lead) { lead = document.createElement('span'); lead.className = 'variant-choice-lead'; lead.textContent = container.dataset.onOrderShort || ''; button.append(lead); }
          if (lead) lead.hidden = !onOrder;
          const price = button.querySelector('.variant-choice-price')?.textContent;
          const label = button.querySelector('.variant-choice-name').textContent + (price ? ` · ${price}` : '');
          const suffix = !matches.length ? container.dataset.incompatibleLabel : soldOut ? container.dataset.soldOutLabel : onOrder ? container.dataset.onOrderShort : '';
          button.title = suffix ? `${label} · ${suffix}` : label;
          button.setAttribute('aria-label', button.title);
          if (kind === 'color') {
            const image = button.querySelector('img');
            const entry = matches.length ? colorisFor(coloris, materialName, button.dataset.value) : null;
            const src = entry?.src || null;
            if (src && image.getAttribute('src') !== src) { image.srcset = entry.srcset || ''; image.src = src; image.hidden = false; }
            if (!src) { image.removeAttribute('src'); image.removeAttribute('srcset'); image.hidden = true; }
            image.parentElement.classList.toggle('variant-swatch--missing', !src || image.hidden);
            button.classList.toggle('variant-choice--text', !src || image.hidden);
          }
        }
        if (!buttons.some(button => button.tabIndex === 0)) {
          const first = buttons.find(button => !button.disabled); if (first) first.tabIndex = 0;
        }
      }
      // The gallery names the fabric chosen, with its macro photograph when the Coloris entry has one.
      if (fabric) {
        const colorGroup = groups.find(group => group.kind === 'color');
        const entry = colorGroup ? colorisFor(coloris, materialName, colorGroup.select.value) : null;
        fabric.hidden = !entry?.src;
        if (entry?.src) {
          const image = fabric.querySelector('img');
          if (image.dataset.src !== entry.src) {
            image.dataset.src = entry.src; image.srcset = entry.srcset || ''; image.src = entry.src;
            fabric.classList.remove('is-changing'); void fabric.offsetWidth; fabric.classList.add('is-changing');
          }
          fabric.querySelector('[data-fabric-material]').textContent = materialName || '';
          fabric.querySelector('[data-fabric-color]').textContent = entry.name || '';
        }
      }
      // The less common native option controls follow the same dependency rules.
      for (const select of options) {
        const position = Number(select.dataset.optionPosition);
        for (const option of select.options) {
          if (!option.value) continue;
          const matches = candidates(variants, selections, order, position, option.value);
          option.hidden = !matches.length;
          option.disabled = !matches.length;
        }
      }
    }
    function reconcile(changed) {
      const position = Number(changed.dataset.optionPosition);
      const next = reconcileSelection(variants, values(), order, position);
      options.forEach((select, index) => { select.value = next[index]; });
      if (feedback) { feedback.hidden = true; feedback.textContent = ''; }
    }
    return { sync, reconcile, focusFirst() { const target = container.querySelector('[role="radio"][tabindex="0"]') || options[0]; target?.scrollIntoView({ block: 'center' }); target?.focus({ preventScroll: true }); } };
  }
  const api = { mount, colorisFor, colorisName, candidates, reconcileSelection };
  if (typeof module !== 'undefined') module.exports = api;
  else root.MSTVariantPicker = api;
})(globalThis);
